import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, TextField, Typography, CircularProgress, useTheme, useMediaQuery, Paper, InputAdornment, Tabs, Tab } from '@mui/material';
import { Search, Contacts as ContactsIcon, AccountCircleOutlined, EventAvailable, CalendarMonthOutlined, AccountBalanceOutlined } from '@mui/icons-material';
import AppLink from '../../AppLink/AppLink';
import { UserConfigurableModule } from '../../../../types/User';
import { DEFAULT_ICONS } from '../../ItemHeader/ItemHeader';
import { humanizeEventDateTimes } from '../../../../utility/dateUtils';
import { searchModalStyles as sx } from './searchModalStyles';
import { Account } from '../../../../types/Account';
import { Event } from '../../../../types/Event';
import { Task } from '../../../../types/Task';
import { Contact } from '../../../../types/Contact';
import { Employee } from '../../../../types/Employee';
import { useDebouncedValue } from '../../../../hooks/useDebounce';
import { useGlobalSearch } from '../../../../services/react-query/globalSearch/useGlobalSearch';

// ---------- Shared Info Row ----------
type InfoItem = { icon?: React.ReactNode; content: React.ReactNode };

function InfoRow({ items }: { items: InfoItem[] }) {
  if (!items.length) return null;
  return (
    <Box sx={sx.infoRow}>
      {items.map((it, idx) => (
        <Box key={idx} sx={sx.infoItem}>
          {it.icon}
          <Typography variant="body2" sx={sx.infoText} title={typeof it.content === 'string' ? it.content : undefined}>
            {it.content}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ---------- Per Module Inline Info (no refetching) ----------
const AccountInfoLine = ({ account }: { account: Account }) => {
  const items: InfoItem[] = [
    account.type_value ? { content: account.type_value } : null,
    account.manager_display_name ? { icon: <AccountCircleOutlined fontSize="small" color="action" />, content: account.manager_display_name } : null,
  ].filter(Boolean) as InfoItem[];
  return <InfoRow items={items} />;
};

const EventInfoLine = ({ event }: { event: Event }) => {
  const items: InfoItem[] = [
    { icon: <CalendarMonthOutlined fontSize="small" />, content: humanizeEventDateTimes(event) },
    event.account_name && { icon: <AccountBalanceOutlined fontSize="small" />, content: event.account_name },
  ].filter(Boolean) as InfoItem[];
  return <InfoRow items={items} />;
};

const TaskInfoLine = ({ task }: { task: Task }) => {
  const items: InfoItem[] = [
    task.event_display_name && { icon: <EventAvailable fontSize="small" />, content: task.event_display_name },
    task.account_name && { icon: <AccountBalanceOutlined fontSize="small" />, content: task.account_name },
  ].filter(Boolean) as InfoItem[];
  return <InfoRow items={items} />;
};

const ContactInfoLine = ({ contact }: { contact: Contact }) => {
  const items: InfoItem[] = [
    contact.contact_title_value && { icon: <ContactsIcon fontSize="small" />, content: contact.contact_title_value },
    contact.account_name && { icon: <AccountBalanceOutlined fontSize="small" />, content: contact.account_name },
  ].filter(Boolean) as InfoItem[];
  return <InfoRow items={items} />;
};

const EmployeeInfoLine = ({ employee }: { employee: Employee }) => {
  const items: InfoItem[] = [employee.title_value && { icon: <AccountCircleOutlined fontSize="small" />, content: employee.title_value }].filter(
    Boolean
  ) as InfoItem[];
  return <InfoRow items={items} />;
};

const TAB_MODEL_MAP: Partial<Record<UserConfigurableModule, string[]>> = {
  [UserConfigurableModule.ACCOUNTS]: ['Account'],
  [UserConfigurableModule.EVENTS]: ['Event'],
  [UserConfigurableModule.TASKS]: ['Task'],
  [UserConfigurableModule.CONTACTS]: ['Contact'],
  [UserConfigurableModule.EMPLOYEES]: ['Employee'],
};

const tabToModels = (tab: 'ALL' | UserConfigurableModule): string[] => {
  if (tab === 'ALL') return [];
  return TAB_MODEL_MAP[tab] ?? [];
};
// ---------- Main Component ----------
export default function SearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'ALL' | UserConfigurableModule>('ALL');

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const debouncedSearch = useDebouncedValue(query, 300);

  const modelsParam = useMemo(() => tabToModels(tab), [tab]);

  // Use React Query hook
  const { data: results = [], isFetching } = useGlobalSearch({ search: debouncedSearch, models: modelsParam }, open);

  // Reset state when closing modal
  useEffect(() => {
    if (!open) {
      setQuery('');
      setTab('ALL');
    }
  }, [open]);

  // Filter results by selected tab
  const filteredResults = useMemo(() => {
    if (!results) return [];
    if (tab === 'ALL') return results;
    return results.filter((r) => r.module === tab);
  }, [results, tab]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => setTab(newValue as 'ALL' | UserConfigurableModule);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={sx.dialog} slotProps={{ paper: { sx: sx.paper } }}>
      {/* Search Field */}
      <Box sx={sx.searchBox}>
        <TextField
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search everywhere..."
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              sx: sx.textFieldInput,
            },
          }}
        />
      </Box>

      {/* Tabs */}
      <Box sx={sx.tabsWrapper}>
        <Tabs value={tab} onChange={handleTabChange} variant={isSmall ? 'scrollable' : 'standard'} scrollButtons={isSmall ? 'auto' : false}>
          <Tab label="All" value="ALL" />
          <Tab label="Accounts" value={UserConfigurableModule.ACCOUNTS} />
          <Tab label="Events" value={UserConfigurableModule.EVENTS} />
          <Tab label="Tasks" value={UserConfigurableModule.TASKS} />
          <Tab label="Contacts" value={UserConfigurableModule.CONTACTS} />
          <Tab label="Employees" value={UserConfigurableModule.EMPLOYEES} />
        </Tabs>
      </Box>

      {/* Results List */}
      <Box sx={sx.resultsContainer}>
        <Paper variant="outlined" sx={sx.resultsPaper}>
          <Box sx={{ p: 1 }}>
            {isFetching ? (
              <Box sx={sx.loadingWrapper}>
                <CircularProgress size={28} />
              </Box>
            ) : !debouncedSearch.trim() ? (
              <Box sx={sx.emptyState}>
                <Typography variant="body2" color="text.secondary">
                  Start typing to search across all modules.
                </Typography>
              </Box>
            ) : filteredResults.length === 0 ? (
              <Box sx={sx.emptyState}>
                <Typography>No results found</Typography>
              </Box>
            ) : (
              filteredResults.map((r) => (
                <Box key={r.uuid} sx={sx.resultRow}>
                  <Box sx={sx.resultIcon}>{DEFAULT_ICONS[r.module]}</Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <AppLink to={r.url}>
                      <Typography sx={sx.resultTitle} title={r.display_name}>
                        {r.display_name}
                      </Typography>
                    </AppLink>

                    {r.module === UserConfigurableModule.ACCOUNTS && <AccountInfoLine account={r as Account} />}
                    {r.module === UserConfigurableModule.EVENTS && <EventInfoLine event={r as Event} />}
                    {r.module === UserConfigurableModule.TASKS && <TaskInfoLine task={r as Task} />}
                    {r.module === UserConfigurableModule.CONTACTS && <ContactInfoLine contact={r as Contact} />}
                    {r.module === UserConfigurableModule.EMPLOYEES && <EmployeeInfoLine employee={r as Employee} />}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Paper>
      </Box>
    </Dialog>
  );
}
